import { z } from "zod";

/*
 * Which releases a package runs on (docs/packages.md, "OS support"): no
 * fixed list of releases. A manifest names the lowest version per
 * distribution and, per distribution, its vendor's apt repository to ask;
 * the panel asks it (cached, `apps/api/src/packages/os-support.ts`) and the
 * bundle's preflight asks it again on the server with its own codename and
 * architecture before changing anything. `tested` lists the releases our
 * scripts were verified on: another release the vendor serves can be
 * installed, with a note.
 */

export const DISTROS = ["ubuntu", "debian", "rocky", "alma", "fedora"] as const;
export const Distro = z.enum(DISTROS);
export type Distro = z.infer<typeof Distro>;

/** `ubuntu-24.04`, `debian-12`. */
export const OS_RELEASE = /^(ubuntu|debian|rocky|alma|fedora)-\d{1,2}(\.\d{1,2})?$/;

/** The placeholders a repository URL may hold, only in its path. */
export const REPOSITORY_PLACEHOLDERS = ["codename", "arch", "version"] as const;

/** A repository URL template: https, a fixed host, placeholders only after the host. */
const RepositoryUrl = z
  .string()
  .max(300)
  .superRefine((template, ctx) => {
    const origin = /^https:\/\/([a-z0-9.-]+)(\/[^\s?#]*)$/.exec(template);
    if (!origin) {
      ctx.addIssue({ code: "custom", message: "a repository URL is https with a fixed host" });
      return;
    }
    const unknown = [...(origin[2] ?? "").matchAll(/\{([^}]*)\}/g)]
      .map((m) => m[1] ?? "")
      .filter((p) => !(REPOSITORY_PLACEHOLDERS as readonly string[]).includes(p));
    if (unknown.length > 0)
      ctx.addIssue({
        code: "custom",
        message: `unknown placeholder ${unknown.join(", ")} (only ${REPOSITORY_PLACEHOLDERS.join(", ")})`,
      });
  });

export const OsRepository = z
  .object({
    /** Answers 200 when the vendor serves the release (an apt suite's `Release`). */
    release: RepositoryUrl,
    /** Optional: non-empty when it serves the architecture too (the suite's `Packages`). */
    packages: RepositoryUrl.optional(),
  })
  .strict();
export type OsRepository = z.infer<typeof OsRepository>;

export const OsSupport = z
  .object({
    /** The lowest version our scripts handle (`22.04`, `12`). */
    min: z.string().regex(/^\d{1,2}(\.\d{1,2})?$/),
    /** The vendor's repository to ask; none: every version from `min` on. */
    repository: OsRepository.optional(),
  })
  .strict();
export type OsSupport = z.infer<typeof OsSupport>;

/** `requires.os`: distributions (any release), or the minimum and the repository per distribution. */
export const RequiresOs = z.union([
  z.array(Distro),
  z.partialRecord(Distro, OsSupport).refine((o) => Object.keys(o).length > 0, {
    message: "name at least one distribution",
  }),
]);
export type RequiresOs = z.infer<typeof RequiresOs>;

/**
 * Codenames of releases, only to ask a vendor's repository from the panel
 * (the provider's image names the version, not the codename). A release
 * missing here is not refused: it is checked on the server, whose
 * /etc/os-release names its own codename.
 */
export const OS_CODENAMES: Readonly<Record<string, string>> = {
  "ubuntu-20.04": "focal",
  "ubuntu-22.04": "jammy",
  "ubuntu-24.04": "noble",
  "ubuntu-24.10": "oracular",
  "ubuntu-25.04": "plucky",
  "ubuntu-25.10": "questing",
  "ubuntu-26.04": "resolute",
  "debian-11": "bullseye",
  "debian-12": "bookworm",
  "debian-13": "trixie",
  "debian-14": "forky",
};

/**
 * Where a release stands for a package:
 * - `tested`: our scripts were verified on it (and the vendor serves it);
 * - `untested`: the vendor serves it, we have not verified it — installable, with a note;
 * - `unchecked`: the panel could not ask (an unknown codename, the repository did not
 *   answer) — installable, the server's preflight asks;
 * - `unavailable`: the vendor's repository does not serve it (or not this architecture);
 * - `unsupported`: a distribution the package does not name, or below its minimum.
 */
export const OS_SUPPORT_STATES = [
  "tested",
  "untested",
  "unchecked",
  "unavailable",
  "unsupported",
] as const;
export const OsSupportState = z.enum(OS_SUPPORT_STATES);
export type OsSupportState = z.infer<typeof OsSupportState>;

export const installableState = (state: OsSupportState) =>
  state === "tested" || state === "untested" || state === "unchecked";

/** `GET /projects/:id/packages/:packageId/os-support`: each server of the project. */
export const PackageOsSupport = z.object({
  servers: z.array(
    z.object({
      serverId: z.uuid(),
      /** `ubuntu-26.04`; null when the image does not say. */
      release: z.string().nullable(),
      arch: z.enum(["amd64", "arm64"]),
      state: OsSupportState,
    }),
  ),
});
export type PackageOsSupport = z.infer<typeof PackageOsSupport>;

const versionParts = (v: string) => v.split(".").map((p) => Number(p));

/** `a >= b` for `22.04`-style versions. */
export function versionAtLeast(a: string, b: string): boolean {
  const [x, y] = [versionParts(a), versionParts(b)];
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const d = (x[i] ?? 0) - (y[i] ?? 0);
    if (d !== 0) return d > 0;
  }
  return true;
}

/** What the manifest says of a release before asking anyone. */
export type OsRequirement =
  { kind: "any" } | { kind: "unsupported" } | { kind: "probe"; repository: OsRepository | null };

export function osRequirement(os: RequiresOs | undefined, release: string): OsRequirement {
  if (!os) return { kind: "any" };
  const [distro = "", version = ""] = release.split("-");
  if (Array.isArray(os))
    return (os as readonly string[]).includes(distro) ? { kind: "any" } : { kind: "unsupported" };
  const support = (os as Partial<Record<string, OsSupport>>)[distro];
  if (!support || !versionAtLeast(version, support.min)) return { kind: "unsupported" };
  return { kind: "probe", repository: support.repository ?? null };
}

/**
 * A repository template filled for one server. The values are checked
 * (a codename is lower-case letters, the version digits and dots, the
 * architecture one we know), so the URL stays on the template's host.
 */
export function repositoryUrl(
  template: string,
  values: { codename: string; arch: string; version: string },
): string | null {
  if (!/^[a-z]{2,20}$/.test(values.codename)) return null;
  if (!/^(amd64|arm64)$/.test(values.arch)) return null;
  if (!/^\d{1,2}(\.\d{1,2})?$/.test(values.version)) return null;
  return template
    .replaceAll("{codename}", values.codename)
    .replaceAll("{arch}", values.arch)
    .replaceAll("{version}", values.version);
}
